import { Container as PixiReactContainer } from "@pixi/react"
import { t } from "i18next"
import type { DisplayObject } from "pixi.js"
import { useCallback, useEffect, useRef, useState } from "react"
import { GRAPH_CONTAINER_NAME, NODE_ANIMATION_DURATION } from "@/domains/visualization/constants/graph"
import { useEdgeAnimation } from "@/domains/visualization/hooks/useEdgeAnimation/useEdgeAnimation"
import { useElkLayout } from "@/domains/visualization/hooks/useElkLayout/useElkLayout"
import { useNodeAnimation } from "@/domains/visualization/hooks/useNodeAnimation/useNodeAnimation"
import { usePanAndZoom } from "@/domains/visualization/hooks/usePanAndZoom"
import { selectCanvasHeight, selectCanvasWidth } from "@/domains/visualization/store/selectors"
import { useGraphStore } from "@/domains/visualization/store/store"
import type { Edge } from "@/domains/visualization/types/Edge"
import type { Vertex } from "@/domains/visualization/types/Vertex"
import { CanvasLoadingSpinner } from "../CanvasLoadingSpinner/CanvasLoadingSpinner.tsx"
import { GraphEdge } from "../GraphEdge/GraphEdge.tsx"
import { GraphNode } from "../GraphNode/GraphNode.tsx"

interface GraphCanvasProps {
  nodes: Vertex[]
  edges: Edge[]
  wrapperRef: React.RefObject<HTMLDivElement>
  isLoading?: boolean
}

export function GraphCanvas({ nodes, edges, wrapperRef, isLoading = false }: GraphCanvasProps) {
  const pendingPanNodeRef = useRef<DisplayObject | null>(null)
  const [layoutReady, setLayoutReady] = useState(false)
  const canvasWidth = useGraphStore(selectCanvasWidth)
  const canvasHeight = useGraphStore(selectCanvasHeight)

  const { positionedNodes, positionedEdges, initialZoom, initialLayoutReady } = useElkLayout(
    nodes,
    edges,
    canvasWidth,
    canvasHeight,
    setLayoutReady
  )

  const { panToNode } = usePanAndZoom({
    containerName: GRAPH_CONTAINER_NAME,
    wrapperRef,
    initialZoom,
    useStore: true
  })

  useEffect(() => {
    if (layoutReady && pendingPanNodeRef.current) {
      // Wait for the node animation to complete before checking if we need to pan
      const timeoutId = setTimeout(() => {
        if (pendingPanNodeRef.current) {
          panToNode(pendingPanNodeRef.current)
        }
        pendingPanNodeRef.current = null
      }, NODE_ANIMATION_DURATION)

      return () => {
        clearTimeout(timeoutId)
      }
    }
  }, [layoutReady, panToNode])

  useNodeAnimation(positionedNodes, pendingPanNodeRef.current, layoutReady)
  useEdgeAnimation(positionedEdges, pendingPanNodeRef.current, layoutReady)

  const handleNodeClickWithInstance = useCallback((instance: DisplayObject) => {
    pendingPanNodeRef.current = instance
    setLayoutReady(false)
  }, [])

  const shouldRenderGraph = canvasWidth > 0 && canvasHeight > 0 && positionedNodes.length > 0

  return (
    <PixiReactContainer name={GRAPH_CONTAINER_NAME}>
      {!initialLayoutReady ? (
        <CanvasLoadingSpinner x={canvasWidth / 2} y={canvasHeight / 2} text={t("visualization.creatingGraph")} />
      ) : (
        shouldRenderGraph && (
          <>
            {positionedEdges.map((edge) => (
              <GraphEdge key={edge.id} edge={edge} />
            ))}
            {positionedNodes.map((node) => (
              <GraphNode key={node.id} node={node} isLoading={isLoading} onNodeClick={handleNodeClickWithInstance} />
            ))}
          </>
        )
      )}
    </PixiReactContainer>
  )
}
